import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Home, BookOpen, ShoppingCart, Users, Sparkles, Calendar, MapPin, UtensilsCrossed, Wallet, Settings, Zap, TrendingDown, Link as LinkIcon, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AIMealPlanDialog from "@/components/AIMealPlanDialog";
import TodoList from "@/components/TodoList";
import WeatherWidget from "@/components/WeatherWidget";
import HomeLayoutDialog from "@/components/home/HomeLayoutDialog";
import UpcomingCalendarCard from "@/components/home/UpcomingCalendarCard";
import { useHomeLayout, resolveSize } from "@/lib/homeLayout";

function getMondayOfWeek(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff)).toISOString().slice(0, 10);
}

function greeting() {
  const h = new Date().getHours();
  if (h < 10) return "God morgen";
  if (h < 17) return "Hei";
  return "God kveld";
}

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];

function WeekDinnerCard({ summary, onNavigate, size = "large" }) {
  if (!summary?.plan) return null;
  const plan = summary.plan;
  const recipes = summary.recipes || [];
  const recipeById = Object.fromEntries(recipes.map((r) => [r.id, r]));

  // Figure out today's weekday index (0=Mon … 6=Sun)
  const now = new Date();
  const dow = now.getDay(); // 0=Sun..6=Sat
  const todayIdx = dow === 0 ? 6 : dow - 1;

  const daySlots = DAY_KEYS.map((k, idx) => {
    const entry = plan?.[k]?.dinner;
    let title = null;
    let emoji = "🍽️";
    if (entry) {
      if (typeof entry === "string") {
        const r = recipeById[entry];
        title = r?.title || null;
      } else if (typeof entry === "object") {
        title = entry.custom_meal || entry.text || (entry.recipe_id ? recipeById[entry.recipe_id]?.title : null);
      }
    }
    // Lightweight emoji detection from title
    if (title) {
      const t = title.toLowerCase();
      if (/fisk|laks|torsk|sei|makrell/.test(t)) emoji = "🐟";
      else if (/kylling/.test(t)) emoji = "🍗";
      else if (/pasta|spaghet/.test(t)) emoji = "🍝";
      else if (/pizza/.test(t)) emoji = "🍕";
      else if (/suppe/.test(t)) emoji = "🥣";
      else if (/salat/.test(t)) emoji = "🥗";
      else if (/taco|burrito|wrap/.test(t)) emoji = "🌮";
      else if (/biff|steik|kjøtt/.test(t)) emoji = "🥩";
      else if (/grønn|grønnsak|vegetar|vegansk/.test(t)) emoji = "🥦";
    }
    return { idx, key: k, label: DAY_LABELS[idx], title, emoji, isToday: idx === todayIdx };
  });

  const today = daySlots[todayIdx];
  const rest = daySlots.filter((d) => d.idx !== todayIdx);

  // Compact mode (small): single row with today + 6 icons, no rest-strip
  if (size === "small") {
    return (
      <button
        onClick={onNavigate}
        className="w-full text-left bg-gradient-to-br from-stone-900 to-amber-900 rounded-xl p-3 shadow-sm hover:shadow-md transition-all flex items-center gap-3"
        data-testid="home-week-dinners"
      >
        <div className="text-3xl">{today.title ? today.emoji : "🤔"}</div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-300/90">I dag · middag</p>
          <p className="text-base font-semibold text-white truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {today.title || "Ingen middag satt"}
          </p>
        </div>
      </button>
    );
  }

  const heroPad = size === "large" ? "p-5 sm:p-6" : "p-4 sm:p-5";
  const heroTitle = size === "large" ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl";
  const heroEmoji = size === "large" ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl";
  const heroBox = size === "large" ? "w-16 h-16 sm:w-20 sm:h-20" : "w-12 h-12 sm:w-16 sm:h-16";

  return (
    <div data-testid="home-week-dinners">
      {/* Today's dinner — big card */}
      <button
        onClick={onNavigate}
        className={`w-full text-left bg-gradient-to-br from-stone-900 via-stone-800 to-amber-900 rounded-2xl ${heroPad} shadow-md hover:shadow-lg transition-all group`}
        data-testid="home-today-dinner"
      >
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 ${heroBox} rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center ${heroEmoji}`}>
            {today.title ? today.emoji : "🤔"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-block text-[10px] uppercase tracking-[0.3em] font-semibold text-amber-300/90">I dag · {today.label}</span>
            </div>
            <h3 className={`${heroTitle} font-semibold text-white leading-tight line-clamp-2`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {today.title || "Ingen middag satt"}
            </h3>
            {!today.title && size === "large" && (
              <p className="text-sm text-amber-100/80 mt-1">Klikk for å planlegge ukens middager →</p>
            )}
          </div>
        </div>
      </button>

      {/* Rest-of-week strip (only medium + large) */}
      {size !== "small" && (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-1.5">
          {rest.map((d) => (
            <button
              key={d.key}
              onClick={onNavigate}
              className="flex flex-col items-center bg-white border border-stone-200 rounded-lg px-2 py-2.5 hover:border-amber-400 hover:bg-amber-50/50 transition-colors text-center min-w-0"
              data-testid={`home-dinner-${d.key}`}
              title={d.title || "Ingen middag satt"}
            >
              <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{d.label.slice(0, 3)}</span>
              <span className="text-xl mt-0.5">{d.title ? d.emoji : "—"}</span>
              <span className="text-[11px] text-stone-700 mt-0.5 line-clamp-1 w-full">{d.title || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { activeBoat, user, loadActiveBoat } = useAuth();
  const activeShift = (user?.preferred_shift === "1" || user?.preferred_shift === "2")
    ? user.preferred_shift
    : (() => { try { return localStorage.getItem("pb-active-shift") || "1"; } catch { return "1"; } })();
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [budget, setBudget] = useState(null);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetInput, setBudgetInput] = useState(0);
  const [modeInput, setModeInput] = useState("relaxed");
  const [storeInput, setStoreInput] = useState("rema");
  const [aiPlanOpen, setAiPlanOpen] = useState(false);
  const [aiInitialCategory, setAiInitialCategory] = useState("mix");
  const [layoutOpen, setLayoutOpen] = useState(false);
  const { layout, update: updateLayout, reset: resetLayout } = useHomeLayout();

  const loadBudget = useCallback(async () => {
    try {
      const res = await api.getBudgetStatus();
      setBudget(res.data);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    if (!activeBoat) return;
    try {
      const ws = getMondayOfWeek();
      const [recipesRes, shopRes, planRes] = await Promise.all([
        api.getRecipes().catch(() => ({ data: [] })),
        api.getShoppingList(ws, "", activeShift).catch(() => ({ data: { items: [], ai_items: [] } })),
        api.getMealPlan(ws, activeShift).catch(() => ({ data: { week_start: ws } })),
      ]);
      setSummary({
        ws,
        recipeCount: (recipesRes.data || []).length,
        shopItems: (shopRes.data?.items || []).length,
        shopDone: (shopRes.data?.items || []).filter((i) => i.checked).length,
        aiItems: (shopRes.data?.ai_items || []).length,
        members: (activeBoat.members || []).filter((m) => !m.is_global_admin).length,
        plan: planRes.data || { week_start: ws },
        recipes: recipesRes.data || [],
      });
    } catch { /* silent */ }
  }, [activeBoat]);

  useEffect(() => { load(); loadBudget(); }, [load, loadBudget]);

  const openBudgetDialog = () => {
    setBudgetInput(activeBoat?.monthly_budget_nok || 0);
    setModeInput(activeBoat?.budget_mode || "relaxed");
    setStoreInput(activeBoat?.preferred_store || "rema");
    setBudgetOpen(true);
  };

  const saveBudget = async () => {
    try {
      await api.updateBoat(activeBoat.id, {
        monthly_budget_nok: Number(budgetInput) || 0,
        budget_mode: modeInput,
        preferred_store: storeInput,
      });
      toast.success("Budsjett oppdatert");
      setBudgetOpen(false);
      loadActiveBoat();
      loadBudget();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    }
  };

  const generateAI = async () => {
    if (!summary) return;
    setGenerating(true);
    try {
      const days = activeBoat?.household_days || 7;
      toast.info(`Genererer handleliste for ${days} dager...`);
      await api.generateAIShoppingList(summary.ws, activeShift);
      toast.success("AI-handleliste oppdatert");
      load();
      navigate("/shopping");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke generere");
    } finally { setGenerating(false); }
  };

  const openAIPlan = (category = "mix") => {
    setAiInitialCategory(category);
    setAiPlanOpen(true);
  };

  const applyHomeAISuggestion = async (sugg) => {
    if (!sugg || !summary) return;
    const MEALS = ["breakfast", "lunch", "dinner"];
    const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const ws = summary.ws;
    try {
      // Load existing plan to preserve any fields
      const existing = await api.getMealPlan(ws, activeShift).catch(() => ({ data: { week_start: ws } }));
      const plan = { ...existing.data, week_start: ws };
      DAYS.forEach((d) => { if (!plan[d]) plan[d] = { breakfast: {}, lunch: {}, dinner: {}, dessert: {}, extras: {}, extra_labels: {} }; });
      const dks = Object.keys(sugg);
      let filled = 0;
      for (let i = 0; i < Math.min(dks.length, 7); i++) {
        const day = DAYS[i];
        const ds = sugg[dks[i]] || {};
        for (const meal of MEALS) {
          if (!ds[meal]) continue;
          const rid = ds[meal].recipe_id;
          const title = ds[meal].recipe_title || "";
          const cm = ds[meal].custom_meal || "";
          if (!rid || rid === "custom_meal" || rid === "null" || rid === "") {
            plan[day][meal] = { recipe_id: null, recipe_title: "", custom_meal: cm || title || "" };
          } else {
            plan[day][meal] = { recipe_id: rid, recipe_title: title, custom_meal: "" };
          }
          filled++;
        }
      }
      await api.updateMealPlan(ws, plan, activeShift);
      toast.success(`${filled} måltider lagret i matplanen`);
      // Auto-regenerate shopping list using the new plan
      toast.info("Oppdaterer handleliste...");
      await api.generateAIShoppingList(ws, activeShift);
      toast.success("Handleliste oppdatert med nye priser");
      load();
      loadBudget();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre plan");
    }
  };

  const switchToSmartAndPlan = async () => {
    try {
      await api.updateBoat(activeBoat.id, {
        monthly_budget_nok: activeBoat.monthly_budget_nok || 0,
        budget_mode: "smart",
        preferred_store: activeBoat.preferred_store || "rema",
      });
      await loadActiveBoat();
      await loadBudget();
      toast.success("Byttet til Smart-modus. Velg sunn eller hverdag for rimelige retter.");
      openAIPlan("sunn");
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  if (!activeBoat) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center">
        <Home className="h-12 w-12 mx-auto text-stone-300 mb-4" />
        <h2 className="text-2xl font-semibold text-stone-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen husstand valgt</h2>
        <p className="text-sm text-stone-500 mt-2">Opprett eller bli med i en husstand for å komme i gang.</p>
        <button onClick={() => navigate("/boats")} className="mt-4 rounded-md bg-amber-600 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-700" data-testid="go-to-boats">
          Gå til hjem-sida
        </button>
      </div>
    );
  }

  if (activeBoat.kind !== "household") {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center">
        <p className="text-sm text-stone-500">Denne siden er for hjemme-husstand. Du har valgt en båt — gå til <button onClick={() => navigate("/")} className="underline text-navy-800">Turnusplan</button> i stedet.</p>
      </div>
    );
  }

  const household = activeBoat;
  const days = household.household_days || 7;

  // Per-widget size resolver with global density fallback
  const sizeOf = (id) => {
    const w = layout.widgets.find((x) => x.id === id);
    return resolveSize(w, layout.density);
  };
  const visible = layout.widgets.filter((w) => !w.hidden);

  // ─── Widget renderers ───
  const renderWeekDinners = (size) => (
    <WeekDinnerCard summary={summary} onNavigate={() => navigate("/plan")} size={size} />
  );

  const renderWeather = (size) => {
    // Weather already adapts via its internal layout; for 'small' we wrap in a compact container
    if (size === "small") {
      return (
        <div className="bg-white border border-stone-100 rounded-xl p-3">
          <WeatherWidget place={household.address || null} accent="amber" />
        </div>
      );
    }
    return <WeatherWidget place={household.address || null} accent="amber" />;
  };

  const renderCalendar = (size) => <UpcomingCalendarCard size={size} />;

  const renderBudget = () => {
    if (!budget) return null;
    const pct = budget.budget_nok > 0 ? Math.min(100, Math.round((budget.spent_this_month_nok / budget.budget_nok) * 100)) : 0;
    const warnOver = budget.budget_nok > 0 && (budget.spent_this_month_nok + budget.planned_this_week_store_nok) > budget.budget_nok;
    return (
      <div className="bg-white border border-stone-100 rounded-xl p-4 sm:p-5" data-testid="budget-banner">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-600" />
            <h3 className="text-base font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Matbudsjett</h3>
            {budget.budget_nok > 0 && (
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${budget.mode === "smart" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {budget.mode === "smart" ? "⚡ Smart" : "💰 Romslig"}
              </span>
            )}
          </div>
          <button onClick={openBudgetDialog} className="p-2 rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-600" data-testid="budget-settings-btn" title="Endre budsjett">
            <Settings className="h-4 w-4" />
          </button>
        </div>
        {budget.budget_nok === 0 ? (
          <button onClick={openBudgetDialog} className="w-full text-center py-4 border-2 border-dashed border-stone-200 rounded-lg text-sm text-stone-500 hover:border-emerald-300 hover:text-emerald-700 transition-colors" data-testid="set-budget-btn">
            + Sett månedlig matbudsjett
          </button>
        ) : (
          <>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{Math.round(budget.spent_this_month_nok)}</span>
              <span className="text-base text-stone-500">av {budget.budget_nok} kr</span>
              <span className={`ml-auto text-sm font-semibold ${budget.remaining_nok < budget.budget_nok * 0.2 ? "text-red-600" : "text-emerald-700"}`}>{Math.round(budget.remaining_nok)} kr igjen</span>
            </div>
            <div className="w-full bg-stone-100 rounded-full h-2 mb-3">
              <div className={`h-2 rounded-full transition-all ${pct > 85 ? "bg-red-500" : pct > 65 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-stone-500">
                <span className="text-[10px] uppercase tracking-wider font-semibold">Denne uken</span>
                <span className="font-bold text-stone-800">{Math.round(budget.planned_this_week_store_nok)} kr</span>
              </div>
              <div className="flex items-center justify-end gap-1.5 text-stone-500">
                <span className="text-[10px] uppercase tracking-wider font-semibold">🛒 {budget.preferred_store}</span>
                <span className="text-[10px]">{budget.receipts_count} kvittering(er)</span>
              </div>
            </div>
            {warnOver && (
              <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-md text-xs text-red-700 space-y-2" data-testid="budget-warn-banner">
                <div className="flex items-start gap-1.5">
                  <span>⚠️</span>
                  <span>Denne ukens handletur kan sprenge budsjettet ditt med ca. <strong>{Math.max(0, Math.round((budget.spent_this_month_nok + budget.planned_this_week_store_nok) - budget.budget_nok))} kr</strong>.</span>
                </div>
                <button onClick={switchToSmartAndPlan} className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 text-white px-3 py-2 text-xs font-semibold hover:bg-emerald-700 transition-colors" data-testid="switch-to-smart-btn">
                  <TrendingDown className="h-3.5 w-3.5" /> Bytt til Smart-modus og foreslå billigere retter
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderStats = (size) => {
    if (!summary) return null;
    // size=small → icon-only; medium=current; large=current with bigger numbers
    if (size === "small") {
      return (
        <div className="grid grid-cols-4 gap-2" data-testid="home-stats">
          <button onClick={() => navigate("/recipes")} className="flex flex-col items-center bg-white border border-stone-100 hover:border-amber-300 rounded-xl py-3" data-testid="stat-recipes" title={`Oppskrifter: ${summary.recipeCount}`}>
            <BookOpen className="h-5 w-5 text-amber-600" />
            <span className="text-xs font-bold text-stone-700 mt-1">{summary.recipeCount}</span>
          </button>
          <button onClick={() => navigate("/shopping")} className="flex flex-col items-center bg-white border border-stone-100 hover:border-amber-300 rounded-xl py-3" data-testid="stat-shopping" title={`Handleliste: ${summary.shopDone}/${summary.shopItems}`}>
            <ShoppingCart className="h-5 w-5 text-amber-600" />
            <span className="text-xs font-bold text-stone-700 mt-1">{summary.shopDone}/{summary.shopItems}</span>
          </button>
          <button onClick={() => navigate("/shopping")} className="flex flex-col items-center bg-white border border-stone-100 hover:border-orange-300 rounded-xl py-3" data-testid="stat-ai" title={`AI-forslag: ${summary.aiItems}`}>
            <Sparkles className="h-5 w-5 text-orange-600" />
            <span className="text-xs font-bold text-stone-700 mt-1">{summary.aiItems}</span>
          </button>
          <button onClick={() => navigate("/boats")} className="flex flex-col items-center bg-white border border-stone-100 hover:border-amber-300 rounded-xl py-3" data-testid="stat-members" title={`Medlemmer: ${summary.members}`}>
            <Users className="h-5 w-5 text-amber-600" />
            <span className="text-xs font-bold text-stone-700 mt-1">{summary.members}</span>
          </button>
        </div>
      );
    }
    const pad = size === "large" ? "p-5" : "p-4";
    const numCls = size === "large" ? "text-3xl" : "text-2xl";
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="home-stats">
        <button onClick={() => navigate("/recipes")} className={`bg-white border border-stone-100 hover:border-amber-300 rounded-xl ${pad} text-left transition-colors`} data-testid="stat-recipes">
          <BookOpen className="h-5 w-5 text-amber-600 mb-2" />
          <div className={`${numCls} font-bold text-stone-800`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{summary.recipeCount}</div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">Oppskrifter</div>
        </button>
        <button onClick={() => navigate("/shopping")} className={`bg-white border border-stone-100 hover:border-amber-300 rounded-xl ${pad} text-left transition-colors`} data-testid="stat-shopping">
          <ShoppingCart className="h-5 w-5 text-amber-600 mb-2" />
          <div className={`${numCls} font-bold text-stone-800`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{summary.shopDone}/{summary.shopItems}</div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">Handleliste</div>
        </button>
        <button onClick={() => navigate("/shopping")} className={`bg-white border border-stone-100 hover:border-amber-300 rounded-xl ${pad} text-left transition-colors`} data-testid="stat-ai">
          <Sparkles className="h-5 w-5 text-orange-600 mb-2" />
          <div className={`${numCls} font-bold text-stone-800`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{summary.aiItems}</div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">AI-forslag</div>
        </button>
        <button onClick={() => navigate("/boats")} className={`bg-white border border-stone-100 hover:border-amber-300 rounded-xl ${pad} text-left transition-colors`} data-testid="stat-members">
          <Users className="h-5 w-5 text-amber-600 mb-2" />
          <div className={`${numCls} font-bold text-stone-800`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{summary.members}</div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider font-semibold">Medlemmer</div>
        </button>
      </div>
    );
  };

  const renderTodo = () => (
    <div data-testid="home-todo-section"><TodoList /></div>
  );

  // Action cards registry (used by renderActions)
  const actionCards = [
    { id: "ai-plan",     label: "AI matplan", desc: `Velg kategori og få en komplett ${days}-dagers meny${budget?.mode === "smart" ? " · Smart-modus" : ""}`, icon: Sparkles, iconBg: "bg-gradient-to-br from-orange-100 to-red-100", iconColor: "text-red-600", border: "hover:border-red-300", onClick: () => openAIPlan("mix"), testid: "home-ai-plan-btn" },
    { id: "plan",        label: "Matplan",    desc: "Se og rediger måltider for hver dag",                                                  icon: Calendar, iconBg: "bg-amber-50", iconColor: "text-amber-600", border: "hover:border-amber-300", onClick: () => navigate("/household-plan"), testid: "quick-plan" },
    { id: "gen-ai",      label: "Generer handleliste",  desc: `Basert på matplanen for ${days} dager · skalert til ${household.num_onboard || 4} personer`, icon: Sparkles, iconBg: "bg-orange-50", iconColor: "text-orange-600", border: "hover:border-orange-300", onClick: generateAI, disabled: generating, testid: "quick-generate-ai" },
    { id: "recipes",     label: "Oppskrifter",          desc: "Importer fra Matprat, Godt.no, Trines matblogg m.m.",                      icon: BookOpen, iconBg: "bg-amber-50", iconColor: "text-amber-600", border: "hover:border-amber-300", onClick: () => navigate("/recipes"), testid: "quick-recipes" },
    { id: "wishes",      label: "Ønskeliste",           desc: "Alle i husstanden kan legge til varer de ønsker",                          icon: UtensilsCrossed, iconBg: "bg-amber-50", iconColor: "text-amber-600", border: "hover:border-amber-300", onClick: () => navigate("/my-lists"), testid: "quick-wishes" },
  ];
  // Add custom shortcuts as extra cards
  const customActions = (layout.customTiles || []).map((t) => ({
    id: t.id,
    label: t.label,
    desc: t.target,
    icon: LinkIcon,
    iconBg: "bg-sky-50",
    iconColor: "text-sky-600",
    border: "hover:border-sky-300",
    onClick: () => {
      if (t.target.startsWith("/")) navigate(t.target);
      else window.open(t.target, "_blank", "noopener,noreferrer");
    },
    testid: `custom-${t.id}`,
  }));
  const allActions = [...actionCards, ...customActions];

  const renderActions = (size) => {
    if (size === "small") {
      // Icon grid (square tiles, label underneath)
      return (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="home-actions">
          {allActions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.id}
                onClick={a.onClick}
                disabled={a.disabled}
                className={`flex flex-col items-center bg-white border border-stone-100 ${a.border} rounded-xl p-3 transition-colors disabled:opacity-50 text-center`}
                data-testid={a.testid}
                title={a.label}
              >
                <div className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`h-5 w-5 ${a.iconColor}`} />
                </div>
                <span className="text-[11px] font-medium text-stone-700 mt-1.5 leading-tight line-clamp-2">{a.label}</span>
              </button>
            );
          })}
        </div>
      );
    }
    if (size === "medium") {
      // 2-col compact rows
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="home-actions">
          {allActions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.id}
                onClick={a.onClick}
                disabled={a.disabled}
                className={`flex items-center gap-3 bg-white border border-stone-100 ${a.border} rounded-xl p-3 text-left transition-colors disabled:opacity-50`}
                data-testid={a.testid}
              >
                <div className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`h-5 w-5 ${a.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-stone-800 truncate">{a.label}</div>
                </div>
              </button>
            );
          })}
        </div>
      );
    }
    // large: current big cards
    return (
      <div className="space-y-3" data-testid="home-actions">
        {allActions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              onClick={a.onClick}
              disabled={a.disabled}
              className={`w-full flex items-center gap-4 bg-white border border-stone-100 ${a.border} rounded-xl p-4 text-left transition-colors disabled:opacity-50`}
              data-testid={a.testid}
            >
              <div className={`w-12 h-12 rounded-xl ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-6 w-6 ${a.iconColor}`} />
              </div>
              <div className="flex-1">
                <div className="text-base font-semibold text-stone-800">{a.label}</div>
                <div className="text-xs text-stone-500 mt-0.5 truncate">{a.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const WIDGET_RENDERERS = {
    "week-dinners": renderWeekDinners,
    "weather":      renderWeather,
    "calendar":     renderCalendar,
    "budget":       renderBudget,
    "stats":        renderStats,
    "todo":         renderTodo,
    "actions":      renderActions,
  };

  const gridCls = layout.columns === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-4";

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="home-page">
      {/* Hero */}
      <div className="mb-6 bg-gradient-to-br from-amber-600 via-orange-600 to-red-500 rounded-2xl p-6 sm:p-8 text-white shadow-lg relative">
        <button
          onClick={() => setLayoutOpen(true)}
          className="absolute top-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white/90 hover:text-white transition-colors"
          data-testid="open-layout-dialog"
          title="Tilpass forsiden"
          aria-label="Tilpass forsiden"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Home className="h-6 w-6" />
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-white/80">Home Edition</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {greeting()}, {user?.name || "velkommen"}
        </h1>
        <p className="text-base text-white/85 mt-1">{household.name}</p>
        {household.address && (
          <div className="flex items-center gap-1.5 mt-2 text-sm text-white/75">
            <MapPin className="h-3.5 w-3.5" />
            {household.address}
          </div>
        )}
        <div className="flex gap-2 mt-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium">
            <Users className="h-3 w-3" /> {household.num_onboard || 4} i husstand
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium">
            <Calendar className="h-3 w-3" /> Planlegger {days} dager
          </span>
        </div>
      </div>

      {/* Dynamic widget layout */}
      <div className={gridCls} data-testid="home-widgets">
        {visible.map((w) => {
          const renderer = WIDGET_RENDERERS[w.id];
          if (!renderer) return null;
          const size = sizeOf(w.id);
          const content = renderer(size);
          if (!content) return null;
          return (
            <div key={w.id} data-widget={w.id} data-size={size}>
              {content}
            </div>
          );
        })}
      </div>

      {/* Layout customization dialog */}
      <HomeLayoutDialog open={layoutOpen} onOpenChange={setLayoutOpen} layout={layout} onUpdate={updateLayout} onReset={resetLayout} />

      {/* Budget dialog */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="max-w-sm" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Wallet className="h-5 w-5 text-emerald-600" /> Matbudsjett
            </DialogTitle>
            <DialogDescription>Angi månedlig budsjett og velg modus + butikk.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Månedlig budsjett (kr)</label>
              <input
                type="number" min="0" step="500"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-base outline-none focus:border-emerald-500"
                data-testid="budget-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block">Budsjettmodus</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setModeInput("smart")}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 text-xs font-semibold transition-all ${modeInput === "smart" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                  data-testid="mode-smart"
                >
                  <Zap className="h-5 w-5" />
                  Smart
                  <span className="text-[10px] font-normal">AI foreslår rimelige retter</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModeInput("relaxed")}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 text-xs font-semibold transition-all ${modeInput === "relaxed" ? "border-amber-600 bg-amber-50 text-amber-800" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                  data-testid="mode-relaxed"
                >
                  <span className="text-xl">💰</span>
                  Romslig
                  <span className="text-[10px] font-normal">Litt ekstra i kassen</span>
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Foretrukken butikk</label>
              <Select value={storeInput} onValueChange={setStoreInput}>
                <SelectTrigger className="mt-1 w-full" data-testid="store-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rema">🛒 Rema 1000</SelectItem>
                  <SelectItem value="kiwi">🛒 Kiwi</SelectItem>
                  <SelectItem value="extra">🛒 Coop Extra</SelectItem>
                  <SelectItem value="coop">🛒 Coop</SelectItem>
                  <SelectItem value="meny">🛒 Meny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button onClick={saveBudget} className="w-full rounded-md bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 transition-colors" data-testid="save-budget-btn">
              Lagre
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Meal Plan dialog */}
      <AIMealPlanDialog
        open={aiPlanOpen}
        onOpenChange={setAiPlanOpen}
        weekStart={summary?.ws}
        initialCategory={aiInitialCategory}
        onApply={applyHomeAISuggestion}
      />
    </div>
  );
}
